// NewAnnouncementForm.tsx - Final version with correct payload
import { Card } from "components/Card";
import ErrorMessage from "components/ErrorMessage";
import Form from "components/Form";
import { RouteProps } from "containers/App";
import ProjectFilter, { FilterOptions } from "containers/ProjectFilter";
import Constants from "helpers/constants";
import { ExtendedCreateAnnouncement, AnnouncementMeta } from "interfaces/announcement";
import { connect, ConnectedProps, useDispatch, useSelector } from "react-redux";
import { State } from "state";
import { createAnnouncement, deleteAnnouncement } from "state/announcement";
import StoreSelect from "./StoreSelect";
import CustomCheckbox from "./CustomCheckbox";
import React, { useEffect, useRef, useState } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { fetchStoresByProjectId, StateWithStores } from "state/project_stores";
import { AttachmentList, FileDropZone } from "./FileDropZone";
import { stringify } from "querystring";
import { SeverityLevel, ToastService } from "services/toastService";
import Loading from "./Loading";
import { trackPageView, PAGEVIEW_EVENTS } from "state/userEvents";
import { fetchMyAnnouncements } from "state/announcements";
import { fetchProject } from "state/project";
import { fetchLoggedUserChains } from "state/logged_user_chains";
import { fetchChainRole } from "state/chain_user_role_fetch";

type NewAnnouncementFormProps = PropsFromRedux & RouteProps& {
    onProjectChange?: (projectId: number | null) => void;
  };

const defaultAnnouncement: ExtendedCreateAnnouncement = {
  project_id: -1,
  title: "",
  body: "",
  store_location_id: -1,
  bcc_recipients: [],
  attachments: [],
  bcc_text: "",
  is_author: false,
};

const CUSTOM_TOOLBAR_OPTIONS = [
  [{ header: "1" }, { header: "2" }, { font: [] }],
  [{ size: [] }],
  ["bold", "italic", "underline", "strike", "blockquote"],
  [{ list: "ordered" }, { list: "bullet" }, { indent: "-1" }, { indent: "+1" }],
  ["link"],
  ["clean"],
];

const modules = { toolbar: CUSTOM_TOOLBAR_OPTIONS };
const formats = [
  "header",
  "font",
  "size",
  "bold",
  "italic",
  "underline",
  "strike",
  "blockquote",
  "list",
  "bullet",
  "indent",
  "link",
  "image",
  "video",
  "clean",
];

// ==================== Helper Functions ====================
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const validateEmail = (email: string): boolean => EMAIL_REGEX.test(email.trim());

const parseEmails = (emailString: string): string[] => {
  if (!emailString.trim()) return [];
  return emailString
    .split(",")
    .map((email) => email.trim())
    .filter((email) => email.length > 0);
};

const validateEmails = (emails: string[]): { valid: string[]; invalid: string[] } => {
  const valid: string[] = [];
  const invalid: string[] = [];
  emails.forEach((email) => {
    if (validateEmail(email)) valid.push(email);
    else invalid.push(email);
  });
  return { valid, invalid };
};

// ==================== Sub Components ====================
const FormHeader: React.FC<{ isSubmitDisabled: boolean }> = ({ isSubmitDisabled }) => (
  <div style={styles.header}>
    <h2>New Announcement</h2>
    <button
      data-qa="submit-announcement"
      disabled={isSubmitDisabled}
      onClick={() => {}}
      type="submit"
      style={{
        ...styles.sendButton,
        opacity: isSubmitDisabled ? 0.6 : 1,
        cursor: isSubmitDisabled ? "not-allowed" : "pointer",
        backgroundColor: "#E5B611",
      }}
    >
      Send Now
    </button>
  </div>
);

const InputField: React.FC<{
  label: string;
  value: string;
  placeholder?: string;
  dataQa: string;
  onChange: (value: string) => void;
  error?: string;
}> = ({ label, value, placeholder = "Announcement title", dataQa, onChange, error }) => (
  <div style={styles.inputGroup}>
    <div style={styles.inputLabel}>
      {label}
      <span style={{ color: "red" }}> *</span>
    </div>
    <Form.Input
      type="text"
      placeholder={placeholder}
      data-qa={dataQa}
      value={value}
      onChange={(event) => onChange(event.currentTarget.value)}
      style={{ ...styles.textInput, borderColor: error ? "#d32f2f" : "#ddd" }}
    />
    {error && <div style={styles.errorText}>{error}</div>}
  </div>
);

const ProjectSelect: React.FC<{
  value: number;
  onChange: (option: FilterOptions) => void;
}> = ({ value, onChange }) => (
  <div style={styles.inputGroup}>
    <div style={styles.inputLabel}>
      Project <span style={{ color: "red" }}> *</span>
    </div>
    <div
      data-qa="select-project"
      style={{ height: 44, border: "0px solid #ddd", borderRadius: 6, boxSizing: "border-box" }}
    >
      <ProjectFilter
        name="select-project"
        value={value}
        isSearchable
        minHeight={44}
        noShadow={true}
        onChange={onChange}
      />
    </div>
  </div>
);

const BccRecipients: React.FC<{
  value: string;
  onChange: (value: string) => void;
  isAuthorChecked: boolean;
  onAuthorChange: (checked: boolean) => void;
  error?: string;
}> = ({ value, onChange, isAuthorChecked, onAuthorChange, error }) => (
  <div style={styles.bccRow}>
    <div style={styles.bccHeader}>
      <span style={styles.bccText}>Optional Recipients (BCC)</span>
      <div style={styles.addAuthorText}>
        <CustomCheckbox
          checked={isAuthorChecked}
          onChange={onAuthorChange}
          label="Add Author"
          checkboxColor="#E3AF32"
          // fillColor="#E3AF32"
        />
      </div>
    </div>
    <Form.Input
      type="text"
      placeholder="email1@example.com, email2@example.com"
      data-qa="bcc-input-NEW-FIELD"
      value={value}
      onChange={(event) => onChange(event.currentTarget.value)}
      style={{ ...styles.textInput, borderColor: error ? "#d32f2f" : "#ddd" }}
    />
    {error && <div style={styles.errorText}>{error}</div>}
    <div style={styles.helperText}>Separate multiple emails with commas</div>
  </div>
);

const AnnouncementBodyEditor: React.FC<{
  value: string;
  onChange: (value: string) => void;
}> = ({ value, onChange }) => {
  const quillRef = useRef<ReactQuill | null>(null);

  useEffect(() => {
    if (quillRef.current) {
      const quill = quillRef.current.getEditor();
      const editorRoot = quill.root;
      if (editorRoot) {
        editorRoot.style.minHeight = "200px";
        editorRoot.style.borderRadius = "16px";
      }
      const quillWrapper = editorRoot.closest(".quill");
      if (quillWrapper) {
        (quillWrapper as HTMLElement).style.height = "auto";
        (quillWrapper as HTMLElement).style.borderRadius = "16px";
      }
    }
  }, []);

  return (
    <div style={{ marginBottom: 15, minHeight: "150px" }}>
      <h3 style={styles.inputLabel}>
        Announcement Body
        <span style={{ color: "red" }}> *</span>
      </h3>
      <div>
        <ReactQuill
          ref={quillRef}
          theme="snow"
          value={value}
          onChange={onChange}
          modules={modules}
          formats={formats}
          style={{ height: "200px" }}
        />
      </div>
    </div>
  );
};

// ==================== Main Component ====================
const NewAnnouncementForm: React.FC<NewAnnouncementFormProps> = (props) => {
  const dispatch = useDispatch();
  const { isMobile } = props;
  const [announcement, setAnnouncement] = useState<ExtendedCreateAnnouncement>(defaultAnnouncement);
  const [meta, setMeta] = useState<AnnouncementMeta>({ project_name: "", project_type: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedStore, setSelectedStore] = useState<number[]>([]);
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null);
  const [emailError, setEmailError] = useState<string>("");

  const projectStoresState = useSelector((state: State) => state.projectStores);
  const fetchedStores: StateWithStores[] = projectStoresState?.content || [];
  const storesLoading: boolean = projectStoresState?.loading || false;
  const storesError: boolean = projectStoresState?.error || false;
  const [loading, setLoading] = useState(false);
  const [isExternalAdmin, setExternalAdmin] = useState(false);

  useEffect(() => {
    if (selectedProjectId && selectedProjectId !== "-1") {
      dispatch(fetchStoresByProjectId({ id: selectedProjectId }));
      setSelectedStore([]);
    }
  }, [selectedProjectId, dispatch]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    let validEmails: string[] = [];
    if (announcement.bcc_text.trim()) {
      const emails = parseEmails(announcement.bcc_text);
      const { valid, invalid } = validateEmails(emails);

      if (invalid.length > 0) {
        setEmailError(`Invalid email(s): ${invalid.join(", ")}`);
        return;
      }
      validEmails = valid;
    }

    setIsSubmitting(true);
    setEmailError("");

    try {
      const formData = new FormData();

      formData.append("project_id", announcement.project_id.toString());
      formData.append("title", announcement.title);
      formData.append("body", JSON.stringify(announcement.body));
      formData.append("storeIds", JSON.stringify(selectedStore));
      formData.append("recipents", JSON.stringify(validEmails));
      formData.append("is_Author", announcement.is_author ? "1" : "0");
      announcement.attachments.forEach((attachment) => {
        formData.append("files", attachment.file);
      });

      // Log FormData for debugging
      // console.log("=== FormData Payload ===");
      // for (let [key, value] of formData.entries()) {
      //   console.log(`${key}:`, value);
      // }

      setLoading(true);

      await dispatch(
        createAnnouncement(formData, {
          name: meta.project_name,
          type: meta.project_type,
        })
      );

      setLoading(false);

      // Call Redux action
      // const response = await props.createAnnouncement(formData, {
      //   name: meta.project_name,
      //   type: meta.project_type,
      // });

      // console.log("=== Create Announcement Response ===", response);
      // console.log("=== Create Announcement Response ===", response.payload[0].id);

      props.fetchMyAnnouncements().then(() => {
        props.trackPageView({
          event_type: PAGEVIEW_EVENTS.ANNOUNCEMENTS_PAGEVIEW,
          user_id: props.user?.id,
          store_id: null,
          project_id: null,
          timestamp: Date.now(),
        });
      });

      ToastService.getInstance().publishToast(
        {
          identifier: "success-operation",
          title: "Success",
          message: "Announcement created successfully!",
        },
        SeverityLevel.SUCCESS,
        { autoClose: 3000 }
      );

      // Reset form after successful submission
      setAnnouncement(defaultAnnouncement);
      setSelectedStore([]);
      setSelectedProjectId(null);

      // console.log("✅ Announcement submitted successfully!");
    } catch (error) {
      console.error("❌ Error submitting announcement:", error);
      ToastService.getInstance().publishToast(
        {
          identifier: "error-operation",
          title: "Error",
          message: "Failed to create announcement. Please try again.",
        },
        SeverityLevel.ERROR,
        { autoClose: 5000 }
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const updateAnnouncement = (updates: Partial<ExtendedCreateAnnouncement>) => {
    setAnnouncement((prev) => ({ ...prev, ...updates }));
  };

  const handleProjectSelect = (id: string) => {
    setSelectedProjectId(id);
  };

  const handleAddAttachment = (file: File) => {
    setAnnouncement((prev) => ({
      ...prev,
      attachments: [
        ...prev.attachments,
        {
          id: Date.now(),
          fileName: file.name,
          fileSize: parseFloat((file.size / (1024 * 1024)).toFixed(1)),
          file: file,
        },
      ],
    }));
  };

  const handleRemoveAttachment = (id: number) => {
    setAnnouncement((prev) => ({
      ...prev,
      attachments: prev.attachments.filter((att) => att.id !== id),
    }));
  };

  const handleEmailChange = (value: string) => {
    updateAnnouncement({ bcc_text: value });
    setEmailError("");
  };

  const getTextContent = (html: string): string => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, "text/html");
    const text = doc.body.textContent;
    return text ? text.trim() : "";
  };

  const {
    content: chains,
  } = useSelector((state: State) => state.logged_user_chains);
    
  // Fetch logged user chains on component mount
  useEffect(() => {
        dispatch(fetchLoggedUserChains());
  }, [dispatch]);
  useEffect(() => {
    // Check if valid project is selected using announcement.project_id
    if (announcement.project_id === -1 || !announcement.project_id) {
      setExternalAdmin(false);
      return;
    }

    if (props.project?.chain?.id && announcement.project_id !== -1) {
      dispatch(fetchChainRole(props.project.chain.id));
    }
  }, [props.project?.chain?.id, announcement.project_id, dispatch]);

  useEffect(() => {
    // Only run this check if there's a valid project selected
    if (announcement.project_id === -1 || !announcement.project_id) {
      setExternalAdmin(false);
      return;
    }
    
    // Wait for chainUserRole to be fetched
    if (!props.chainUserRole) {
      return;
    }
    
    if (props.user?.role !== Constants.AB_SYSTEM_ADMIN) {
      const hasExternalAdmin = props.chainUserRole?.some((item: any) => {
        if (Array.isArray(item.roles)) {
          return item.roles.some((r: any) => r.role === "External Project Admin");
        }
        return item.role === "External Project Admin";
      });
      
      setExternalAdmin(hasExternalAdmin || false);
    } else {
      setExternalAdmin(false);
    }
  }, [props.user?.role, props.chainUserRole, announcement.project_id]);

  const isFormInvalid =
    announcement.project_id === -1 ||
    announcement.title.trim() === "" ||
    getTextContent(announcement.body) === "";
  // ||    selectedStore.length === 0;

  const submitDisabled = isSubmitting || isFormInvalid;

  if (loading) {
    return <Loading />;
  }

  return (
    <div
      style={{
        gap: 0,
        borderRadius: "16px",
        padding: "5px",
        backgroundColor: "#fff",
        boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
      }}
    >
      <Card style={styles.container}>
        <Form onSubmit={handleSubmit} style={{ width: "100%" }}>
          <FormHeader isSubmitDisabled={submitDisabled} />

          <div style={styles.inputRow(isMobile)}>
            <InputField
              label="Announcement Title"
              value={announcement.title}
              dataQa="announcement-title-field"
              onChange={(value) => updateAnnouncement({ title: value })}
            />

            <ProjectSelect
              value={announcement.project_id}
              onChange={({ id, name, type }: FilterOptions) => {
                updateAnnouncement({ project_id: id });
                handleProjectSelect(id.toString());
                setMeta({ project_name: name, project_type: type });
                props.onProjectChange?.(id === -1 ? null : id);
                if (!id || id === -1) {
                  // When deselecting, clear the project and reset external admin
                  setExternalAdmin(false);
                } else {
                  dispatch(fetchProject(id.toString()));
                }
              }}
            />

            <div style={{ width: isMobile ? "100%" : "33%", marginBottom: 0 }}>
              <StoreSelect
                value={selectedStore}
                states={fetchedStores}
                onChange={(id) => setSelectedStore(Array.isArray(id) ? id : [id])}
                multiSelect={true}
                isDisabled={storesLoading || !selectedProjectId || selectedProjectId === "-1"}
              />
              {storesLoading && (
                <div style={{ fontSize: 12, color: "#666", marginTop: 4 }}>Loading stores...</div>
              )}
              {storesError && (
                <div style={{ fontSize: 12, color: "#d32f2f", marginTop: 4 }}>
                  Error loading stores
                </div>
              )}
            </div>
          </div>

        {!isExternalAdmin && announcement.project_id !== -1 && (
          <BccRecipients
            value={announcement.bcc_text}
            onChange={handleEmailChange}
            isAuthorChecked={announcement.is_author}
            onAuthorChange={(checked) => updateAnnouncement({ is_author: checked })}
            error={emailError}
          />
        )}

          <AnnouncementBodyEditor
            value={announcement.body}
            onChange={(value) => updateAnnouncement({ body: value })}
          />

          <FileDropZone onFileAdd={handleAddAttachment} />

          <AttachmentList
            attachments={announcement.attachments}
            onRemove={handleRemoveAttachment}
          />
        </Form>
      </Card>

      <div hidden={!props.hasErrors}>
        <ErrorMessage errors={props.errors || []} />
      </div>
    </div>
  );
};

// ==================== Redux Connection ====================
const connector = connect(
  (state: State) => ({
    user: state.auth_user.content,
    hasErrors: state.surveys.hasError,
    errors: state.surveys.errors,
    isMobile: state.breakpoint.content === Constants.BREAKPOINT_MOBILE,
    currentUserId: state.auth_user.content?.id,
    isAdmin: state.auth_user.content?.role === Constants.AB_SYSTEM_ADMIN,
    loading: state.announcement.loading,
    project: state.project.content,
    chainUserRole: state.chainUserRole.chainUserRole
  }),
  { fetchMyAnnouncements, createAnnouncement, deleteAnnouncement, trackPageView, fetchProject , fetchChainRole}
);

type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(NewAnnouncementForm);

const styles = {
  container: {
    backgroundColor: "#fff",
    padding: 20,
    borderRadius: 16,
    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
    width: "100%",
    boxSizing: "border-box" as const,
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 20,
  },
  title: { fontSize: 24, fontWeight: "normal" as const, color: "#333", margin: 0 },
  sendButton: {
    backgroundColor: "#E5B611",
    color: "#000000",
    border: "none",
    padding: "9px 15px",
    borderRadius: 8,
    cursor: "pointer",
    fontSize: 12,
    fontWeight: 600,
  },
  inputRow: (isMobile: boolean): React.CSSProperties => ({
    display: "flex",
    flexDirection: isMobile ? "column" : "row",
    gap: 20,
    marginBottom: 20,
  }),
  inputGroup: { display: "flex", flexDirection: "column" as const, flex: 1 },
  inputLabel: { fontSize: 14, color: "#333", fontWeight: 400, marginBottom: 8 },
  textInput: {
    padding: "12px 15px",
    border: "1px solid #ddd",
    borderRadius: 16,
    fontSize: 14,
    backgroundColor: "#fff",
    height: 44,
    boxSizing: "border-box" as const,
    width: "100%",
  },
  bccRow: { marginBottom: 20 },
  bccHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 6,
  },
  bccText: { fontSize: 14, color: "#333", fontWeight: 500 },
  addAuthorText: {
    fontSize: 15,
    color: "#3498db",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: 5,
    userSelect: "none" as const,
  },
  errorText: { fontSize: 12, color: "#d32f2f", marginTop: 4 },
  helperText: { fontSize: 12, color: "#666", marginTop: 4 },
} as const;
